import React, { createContext, useContext, useState, ReactNode } from "react";

type ConfirmOptions = {
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
};

type ConfirmContextType = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context.confirm;
};

interface ConfirmProviderProps {
  children: ReactNode;
}

export const ConfirmProvider: React.FC<ConfirmProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [promiseResolver, setPromiseResolver] = useState<((value: boolean) => void) | null>(null);

  const confirm = (opts: ConfirmOptions): Promise<boolean> => {
    console.log("Confirm called with:", opts); // Debug log
    setOptions(opts);
    setIsOpen(true);

    return new Promise<boolean>((resolve) => {
      setPromiseResolver(() => resolve);
    });
  };

  const handleConfirm = () => {
    console.log("Confirmed"); // Debug log
    if (promiseResolver) {
      promiseResolver(true);
    }
    closeDialog();
  };

  const handleCancel = () => {
    console.log("Cancelled"); // Debug log
    if (promiseResolver) {
      promiseResolver(false);
    }
    closeDialog();
  };

  const closeDialog = () => {
    setIsOpen(false);
    setOptions(null);
    setPromiseResolver(null);
  };

  //console.log("Dialog state:", { isOpen, options }); // Debug log

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {isOpen && options && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              margin: "16px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
          >
            {options.title && (
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  marginBottom: "8px",
                  color: "#111827",
                  margin: "0 0 8px 0",
                }}
              >
                {""}
              </h3>
            )}
            <p
              style={{
                color: "#374151",
                marginBottom: "24px",
                margin: "0 0 24px 0",
              }}
            >
              {options.message}
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
              }}
            >
              <button
                style={{
                  padding: "8px 16px",
                  color: "#374151",
                  backgroundColor: "#e5e7eb",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "background-color 0.2s",
                }}
                onClick={handleCancel}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#d1d5db")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#e5e7eb")}
              >
                {options.cancelText || "Cancel"}
              </button>
              <button
                style={{
                  padding: "8px 16px",
                  color: "white",
                  backgroundColor: "#dc2626",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  transition: "background-color 0.2s",
                }}
                onClick={handleConfirm}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#b91c1c")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#dc2626")}
              >
                {options.confirmText || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

// Complete working example component
export const ConfirmExample: React.FC = () => {
  const confirm = useConfirm();

  const handleTestConfirm = async () => {
    try {
      const result = await confirm({
        title: "Test Dialog",
        message: "This is a test confirmation dialog. Do you want to proceed?",
        confirmText: "Yes, proceed",
        cancelText: "No, cancel",
      });

      if (result) {
        alert("You confirmed!");
      } else {
        alert("You cancelled!");
      }
    } catch (error) {
      console.error("Error with confirm dialog:", error);
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Confirm Dialog Test</h2>
      <button
        onClick={handleTestConfirm}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
      >
        Test Confirm Dialog
      </button>
    </div>
  );
};

// Usage in your App.tsx:
/*
import { ConfirmProvider, ConfirmExample } from './path-to-this-file';

function App() {
  return (
    <ConfirmProvider>
      <div className="App">
        <ConfirmExample />
        // ... your other components
      </div>
    </ConfirmProvider>
  );
}
*/
